import { BILLING_CURRENCIES, DEFAULT_TIMEZONE, HOME_COUNTRY, type BillingCurrencyCode } from "@sagot/constants";
import { pickTimezone, type CountryEntry } from "../lib/countries";

export interface WorkspaceRegion {
  country: string;
  currency: BillingCurrencyCode;
  timezone: string;
  language: "en" | "fil";
}

const FALLBACK_CURRENCY: BillingCurrencyCode = "USD";
const FALLBACK_TIMEZONE = "UTC";

export function asCurrency(code: string | null | undefined): BillingCurrencyCode | null {
  return BILLING_CURRENCIES.find((known) => known === code) ?? null;
}

interface LocaleWithZones {
  timeZones?: readonly string[];
  getTimeZones?: () => readonly string[];
}

/** First IANA zone the browser knows for a region (`Intl.Locale` time zones), else UTC. */
function browserTimezone(country: string): string {
  try {
    const locale = new Intl.Locale(`und-${country}`) as Intl.Locale & LocaleWithZones;
    const zones = locale.timeZones ?? locale.getTimeZones?.() ?? [];
    return zones[0] ?? FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

/** The visitor's own zone (`Intl.DateTimeFormat`), when the browser reports one. */
function localTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function timezoneFor(country: string, entry: CountryEntry | undefined): string {
  if (country === HOME_COUNTRY) return DEFAULT_TIMEZONE;
  return pickTimezone(entry, localTimezone()) ?? browserTimezone(country);
}

/**
 * Defaults when the country changes (DEC-042): the server's currency hint (PH → PHP, else USD) and
 * a zone of the country (the visitor's own when it is one). `entry` comes from the country list;
 * without it PH keeps its fixed defaults and other countries bill in USD on the browser's guess.
 */
export function regionDefaults(country: string, language: "en" | "fil" = "en", entry?: CountryEntry): WorkspaceRegion {
  const hint = asCurrency(entry?.default_currency);
  return {
    country,
    currency: hint ?? (country === HOME_COUNTRY ? "PHP" : FALLBACK_CURRENCY),
    timezone: timezoneFor(country, entry),
    language: country === HOME_COUNTRY ? language : "en",
  };
}

/** Region of an existing workspace (API values), defaulting from its country. */
export function regionOf(workspace: { country?: string | null; billing_currency?: string | null; timezone?: string | null; default_language?: string | null } | undefined): WorkspaceRegion {
  const country = workspace?.country ?? HOME_COUNTRY;
  const base = regionDefaults(country);
  return {
    country,
    currency: asCurrency(workspace?.billing_currency) ?? base.currency,
    timezone: workspace?.timezone ?? (country === HOME_COUNTRY ? DEFAULT_TIMEZONE : base.timezone),
    language: workspace?.default_language === "fil" ? "fil" : "en",
  };
}
